import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { QRCodeSVG } from 'qrcode.react'
import { CalendarClock, Car, CreditCard, Plus, Ticket } from 'lucide-react'
import { getReservationList } from '../../lib/api/auth'
import { demoByEmail } from '../../lib/demo'
import { useAuth } from '../../store/authStore'
import { TicketModal } from './TicketModal'
import { PageShell } from '../../components/layout/PageShell'
import { Skeleton, ErrorState } from '../../components/ui/States'
import { formatCents } from '../../lib/money'
import { hhmm, prettyDayShort } from '../../lib/format'
import type { Locale, ReservationListItem } from '../../lib/api/types'
import { cn } from '../../lib/cn'

type Tab = 'active' | 'archived'

export function MyBookings() {
  const { t, i18n } = useTranslation()
  const locale = (i18n.language as Locale) ?? 'it'
  const { locale: routeLocale = 'it' } = useParams()
  const status = useAuth((s) => s.status)
  const user = useAuth((s) => s.user)
  const demo = demoByEmail(user?.email)
  const [tab, setTab] = useState<Tab>('active')
  const [ticket, setTicket] = useState<ReservationListItem | null>(null)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['reservationList', user?.email],
    queryFn: () => (demo ? Promise.resolve(demo.reservations) : getReservationList()),
    enabled: status === 'authenticated',
  })

  const active = data?.activeList ?? []
  const archived = data?.archivedList ?? []
  const list = useMemo(() => (tab === 'active' ? active : archived), [tab, active, archived])

  if (status === 'unknown')
    return (
      <PageShell desktopWide>
        <Skeleton className="h-8 w-48" />
        <div className="mt-6 space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      </PageShell>
    )
  if (status === 'guest')
    return <Navigate to={`/${routeLocale}/login?redirect=/${routeLocale}/bookings`} replace />

  const TABS: { key: Tab; label: string; count?: number }[] = [
    { key: 'active', label: t('bookings.active'), count: active.length },
    { key: 'archived', label: t('bookings.archived'), count: archived.length },
  ]

  return (
    <PageShell desktopWide>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-ink-900 sm:text-3xl">
            {t('bookings.title')}
          </h1>
          <p className="mt-1.5 text-sm text-ink-500">{t('bookings.subtitle')}</p>
        </div>
        <Link
          to={`/${routeLocale}/book`}
          className="inline-flex h-11 items-center gap-2 rounded-xl bg-brand-600 px-4 text-sm font-semibold text-white transition hover:bg-brand-700"
        >
          <Plus className="h-4 w-4" /> {t('bookings.newBooking')}
        </Link>
      </div>

      {/* tabs — one row; scroll horizontally on mobile if the labels don't fit (no wrapping) */}
      <div className="mb-5 -mx-4 overflow-x-auto px-4 [scrollbar-width:none] sm:mx-0 sm:px-0 [&::-webkit-scrollbar]:hidden">
        <div className="inline-flex gap-1 rounded-xl bg-ink-100 p-1">
          {TABS.map((tb) => (
            <button
              key={tb.key}
              onClick={() => setTab(tb.key)}
              className={cn(
                'whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors',
                tab === tb.key ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-800',
              )}
            >
              {tb.label}
              {tb.count != null && (
                <span className={cn('ml-1.5 text-xs', tab === tb.key ? 'text-brand-600' : 'text-ink-400')}>{tb.count}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {isError ? (
        <ErrorState onRetry={() => refetch()} />
      ) : isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      ) : list.length === 0 ? (
        <EmptyState tab={tab} routeLocale={routeLocale} />
      ) : (
        <div className="grid gap-3 lg:grid-cols-2 lg:items-start">
          {list.map((r) => (
            <ReservationCard key={r.code} r={r} locale={locale} onOpen={() => setTicket(r)} />
          ))}
        </div>
      )}

      {ticket && <TicketModal r={ticket} onClose={() => setTicket(null)} />}
    </PageShell>
  )
}

function statusStyle(status: string): string {
  const s = (status || '').toUpperCase()
  if (s.startsWith('CANCEL')) return 'bg-brand-100 text-brand-700'
  if (s === 'DRAFT' || s === 'PENDING') return 'bg-amber-100 text-amber-700'
  return 'bg-green-100 text-green-700'
}
function statusLabel(t: (k: string) => string, status: string): string {
  const s = (status || '').toUpperCase()
  if (s.startsWith('CANCEL')) return t('bookings.statusCancelled')
  if (s === 'DRAFT' || s === 'PENDING') return t('bookings.statusDraft')
  return t('bookings.statusConfirmed')
}

function ReservationCard({ r, locale, onOpen }: { r: ReservationListItem; locale: Locale; onOpen: () => void }) {
  const { t } = useTranslation()
  return (
    <button
      onClick={onOpen}
      className="relative flex w-full items-stretch overflow-hidden rounded-2xl bg-white text-left shadow-card ring-1 ring-ink-100 transition hover:shadow-card-hover"
    >
      {/* left: code + status, then details */}
      <div className="flex min-w-0 flex-1 flex-col p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm font-bold text-ink-900">{r.code}</span>
          <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-semibold', statusStyle(r.status))}>
            {statusLabel(t, r.status)}
          </span>
        </div>
        <div className="mt-3 space-y-2 text-sm text-ink-600">
          <span className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 flex-none text-ink-400" />
            {prettyDayShort(r.arrivalDay, locale)} {hhmm(r.arrivalTime)} → {prettyDayShort(r.departureDay, locale)}{' '}
            {hhmm(r.departureTime)}
          </span>
          <span className="flex items-center gap-2">
            <Car className="h-4 w-4 flex-none text-ink-400" />
            {r.transportData?.label ?? '—'}
            {r.customerData?.licensePlate ? ` · ${r.customerData.licensePlate}` : ''}
          </span>
          {r.paymentModeData?.label && (
            <span className="flex items-center gap-2">
              <CreditCard className="h-4 w-4 flex-none text-ink-400" />
              {r.paymentModeData.label}
            </span>
          )}
        </div>
      </div>

      {/* perforation: dashed divider + punch-hole notches */}
      <div className="relative w-0 self-stretch">
        <span className="absolute -top-2 left-1/2 h-4 w-4 -translate-x-1/2 rounded-full bg-ink-50" />
        <span className="absolute -bottom-2 left-1/2 h-4 w-4 -translate-x-1/2 rounded-full bg-ink-50" />
        <span className="absolute inset-y-3 left-1/2 -translate-x-1/2 border-l-2 border-dashed border-ink-200" />
      </div>

      {/* right stub: price + QR */}
      <div className="flex min-w-[116px] flex-none flex-col items-center justify-center gap-2 bg-ink-50/40 px-4 py-4 sm:min-w-[128px] sm:px-5">
        <span className="font-display text-lg font-extrabold text-ink-900">{formatCents(r.totalPrice, locale)}</span>
        <span className="rounded-md border border-ink-100 bg-white p-1">
          <QRCodeSVG value={r.code} size={52} level="M" marginSize={0} />
        </span>
      </div>
    </button>
  )
}

function EmptyState({ tab, routeLocale }: { tab: Tab; routeLocale: string }) {
  const { t } = useTranslation()
  const msg = tab === 'active' ? t('bookings.emptyActive') : t('bookings.emptyArchived')
  return (
    <div className="gsm-card flex flex-col items-center justify-center px-6 py-16 text-center">
      <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-ink-100 text-ink-400">
        <Ticket className="h-7 w-7" />
      </span>
      <p className="text-ink-500">{msg}</p>
      <Link
        to={`/${routeLocale}/book`}
        className="mt-5 inline-flex h-11 items-center gap-2 rounded-xl bg-brand-600 px-4 text-sm font-semibold text-white transition hover:bg-brand-700"
      >
        <Plus className="h-4 w-4" /> {t('bookings.newBooking')}
      </Link>
    </div>
  )
}
